import { Injectable, computed, inject, signal } from '@angular/core';
import { UserState } from '@coin/shared/auth/feature';
import { CommunicationTypeEnum, TodoTaskType } from '@coin/shared/core/util-enums';
import { DateOperations } from '@coin/shared/core/util-helpers';
import { Topic } from '@coin/shared/core/util-models';
import { produce } from 'immer';
import { Observable, of, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CosmosDashboardService } from '@coin/shared/core/data-access';

export interface BannerStateModel {
  bannerTopics: Topic[];
  loaded: boolean;
  loading: boolean;
}

// Auto-migrated from NGXS
@Injectable({ providedIn: 'root' })
export class BannerState {
  private readonly dashboardService = inject(CosmosDashboardService);
  private readonly userState = inject(UserState);

  private readonly bannerStateModel = signal<BannerStateModel>({
    bannerTopics: undefined,
    loaded: false,
    loading: false
  });

  public visibleBannerTopics = computed(() =>
    this.bannerStateModel().bannerTopics?.filter(
      topic =>
        topic.type === CommunicationTypeEnum.Banner &&
        topic.showBanner &&
        !DateOperations.dueDateOverBasedOnDay(topic.dateEnd)
    )
  );

  loadTopics(): Observable<void> {
    this.bannerStateModel.update(state => ({ ...state, loading: true }));

    return this.dashboardService.getBannerTopics().pipe(
      switchMap(topics => this.loadTopicsSuccess(topics)),
      catchError(() => this.loadTopicsFail())
    );
  }

  private loadTopicsSuccess(payload: Topic[]): Observable<void> {
    const { bannerTopics } = this.bannerStateModel();
    const newBannerTopics = this.mapBannerTopics(
      bannerTopics,
      payload.filter(topic => topic.type === CommunicationTypeEnum.Banner)
    );
    const topics = payload.filter(topic => topic.type !== CommunicationTypeEnum.Banner);
    this.bannerStateModel.update(state => ({ ...state, bannerTopics: newBannerTopics }));
    this.bannerStateModel.update(state => ({ ...state, loaded: true, loading: false }));

    const hasFinalIncumbentTodo = topics.some(
      topic => topic.type === CommunicationTypeEnum.Task && topic.taskType === TodoTaskType.FinalIncumbent
    );
    this.userState.setDynamicMercerAccess(hasFinalIncumbentTodo);
    return of(undefined);
  }

  updateBannerVisibility(payload: Topic): void {
    this.bannerStateModel.update(state =>
      produce(state, draft => {
        const foundIndex = draft.bannerTopics.findIndex(topic => topic.id === payload.id);
        if (foundIndex >= 0) {
          draft.bannerTopics[foundIndex].showBanner = false;
        }
      })
    );
  }

  private loadTopicsFail(): Observable<void> {
    this.bannerStateModel.update(state => ({ ...state, loaded: false, loading: false }));
    this.userState.setDynamicMercerAccess(false);
    return of(undefined);
  }

  mapBannerTopics(currentTopics: Topic[], newTopics: Topic[]): Topic[] {
    return newTopics.map(topic => {
      const currentTopic = currentTopics?.find(currentTopic => currentTopic.id === topic.id);
      if (currentTopic) {
        topic.showBanner = currentTopic.showBanner;
      }
      return topic;
    });
  }
}
